import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import Grid from '@mui/material/Grid2';
import { useErrorNotification } from "@common/components/Notification";
import { WithGrow } from "@common/components/WithGrow";
import { canEditElogsAndRevisions } from "@common/utils/user";
import { LinearProgress } from "@mui/material";
import { getMeetingElog, putMeetingElog } from "@services/meetings";
import {
  ElogBillingCode,
  ElogBillingEntry,
  ElogParticipant,
  MeetingElog,
} from "@services/meetings/types";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import React, { useContext, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import Card, { CardHeader } from "../common/Card";
import Controls from "../common/Controls";
import LabeledText, { LabeledElementGroup } from "../common/LabeledText";
import { getSortFn, getSortOptions } from "./const";
import Drawer from "./Drawer";

const Billing = () => {
  const context = useContext(RoomsRealtimeContext);
  const [user] = useAuthenticatedUser();
  const meeting = { ...context.detail?.meeting };
  const canEdit = canEditElogsAndRevisions(user);
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);
  const [selected, setSelected] = useState<ElogBillingCode["id"][]>([]);
  const [elog, setElog] = useState<MeetingElog>();
  const [sortIndex, setSortIndex] = useState<number>(0);
  const [open, setOpen] = useState<false | Partial<ElogBillingEntry>>(false);
  const sortOptions = getSortOptions(t);
  const dispatchError = useErrorNotification();

  const refetch = async (id: typeof meeting.id) => {
    try {
      setLoading(true);
      setElog(await getMeetingElog(id));
    } catch (e) {
      dispatchError(e);
    } finally {
      setLoading(false);
    }
  };
  const handleSave = useCallback(
    async (billingEntry: Partial<ElogBillingEntry>) => {
      if (elog === undefined || meeting?.id === undefined) return;
      let elogParticipants = elog.elogParticipants ?? [];
      let elogBillingCodes = elog.elogBillingCodes ?? [];
      const applyBillingEntry = <T extends ElogBillingCode | ElogParticipant>(
        x: T
      ): T =>
        billingEntry.id === x.id
          ? {
              ...x,
              billingCode: billingEntry.billingCode,
              quantity: billingEntry.quantity,
            }
          : x;
      if (billingEntry.id === undefined)
        elogBillingCodes.push(billingEntry as ElogBillingCode);
      else {
        if (billingEntry.roomName === undefined)
          elogBillingCodes = elogBillingCodes.map(applyBillingEntry);
        else elogParticipants = elogParticipants.map(applyBillingEntry);
      }
      try {
        await putMeetingElog(meeting.id, {
          ...elog,
          elogParticipants,
          elogBillingCodes,
        });
        await refetch(meeting.id);
        setOpen(false);
      } catch (e) {
        dispatchError(e);
      }
    },
    [elog, meeting?.id]
  );

  const handleSelect = (id: ElogBillingCode["id"]) =>
    setSelected((selected) =>
      selected.includes(id)
        ? selected.filter((x) => x !== id)
        : [...selected, id]
    );

  const handleDelete = useCallback(async () => {
    try {
      if (elog === undefined) return;
      setLoading(true);
      setSelected([]);
      await putMeetingElog(meeting.id, {
        ...elog,
        elogBillingCodes: (elog?.elogBillingCodes ?? []).filter(
          ({ id }) => !selected.includes(id)
        ),
      });
      await refetch(meeting?.id);
    } catch (e) {
      dispatchError(e);
    } finally {
      setLoading(false);
    }
  }, [selected, elog, meeting?.id]);

  useEffect(() => {
    if (meeting?.id !== undefined) refetch(meeting.id);
  }, [context.detail?.meeting?.id]);

  return (
    <>
      <WithGrow
        direction="column"
        fixed={
          <Controls
            options={sortOptions.map(({ label }, value) => ({ label, value }))}
            selected={sortIndex}
            onSelect={setSortIndex}
            onAdd={() => setOpen({})}
            onDelete={handleDelete}
            onRefresh={() => refetch(meeting.id)}
            canEdit={canEdit}
            deleteDisabled={selected.length === 0}
            DeleteDialogProps={{
              actionLabel: t(
                `Delete billing report${selected.length === 1 ? "" : "s"}`
              ),
              confirmPrompt: t(
                selected.length === 1
                  ? "Are you sure you want to delete this billing report?"
                  : "Are you sure you want to delete {{count}} billing reports?",
                { count: selected.length }
              ),
            }}
          />
        }
        containerSx={{ overflow: "hidden" }}
      >
        <Grid
          container
          direction="column"
          wrap="nowrap"
          sx={{ width: '100%', overflowY: "scroll",
            height: "100%",
            py: 0.5, }}
        >
          {meeting === undefined || elog === undefined || loading ? (
            <LinearProgress />
          ) : (
            [
              ...(elog.elogBillingCodes ?? []),
              ...(elog.elogParticipants ?? []).filter(
                ({ billingCode }) => billingCode
              ),
            ]
              .sort(getSortFn(sortOptions[sortIndex].value))
              .map(
                ({
                  id,
                  description,
                  billingCode,
                  quantity,
                  ...participant
                }: ElogBillingCode | ElogParticipant) => (
                  <Card key={id}>
                    <CardHeader
                      disabled={!canEdit}
                      onSelect={() => handleSelect(id)}
                      onEdit={() =>
                        setOpen({
                          id,
                          description,
                          billingCode,
                          quantity,
                          roomName: (participant as ElogParticipant).room?.name,
                        })
                      }
                    >
                      <LabeledElementGroup
                        items={[
                          {
                            label: "Billing Code",
                            value: billingCode,
                            flex: 2,
                          },
                          { label: "Quantity", value: quantity },
                        ]}
                      />
                    </CardHeader>
                    <LabeledText
                      label="Room"
                      value={(participant as ElogParticipant).room?.name ?? ""}
                    />
                    <LabeledText label="Description" value={description} />
                  </Card>
                )
              )
          )}
        </Grid>
      </WithGrow>
      <Drawer
        open={Boolean(open)}
        onClose={() => setOpen(false)}
        onSave={handleSave}
        billingEntry={open ? open : undefined}
      />
    </>
  );
};

export default Billing;
